import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Activity, Search, Filter as FilterIcon, X, ChevronLeft, ChevronRight, Calendar,
  User as UserIcon, Layers, Plus, Trash2, Pencil, RotateCw, CheckCircle, XCircle,
  ArrowRightLeft, Lock, Unlock, FileText, Bookmark, Ticket, Receipt, Users as UsersIcon,
  Bell, Gavel, ClipboardList, Settings as SettingsIcon, Sparkles, Database, RefreshCw,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

// Friendly label for an action token
function prettyAction(a) {
  if (!a) return 'event';
  return a
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

// Pick an icon + colour family for a given (action, module) pair
function actionVisual(action, module) {
  const a = (action || '').toLowerCase();
  if (a.includes('login')) return { Icon: UserIcon, tint: 'bg-blue-50 text-blue-600 ring-blue-100' };
  if (a.includes('create') || a.includes('upload') || a === 'submission_received') return { Icon: Plus, tint: 'bg-emerald-50 text-emerald-600 ring-emerald-100' };
  if (a.includes('delete')) return { Icon: Trash2, tint: 'bg-rose-50 text-rose-600 ring-rose-100' };
  if (a.includes('update') || a.includes('mapping') || a.includes('settings')) return { Icon: Pencil, tint: 'bg-amber-50 text-amber-700 ring-amber-100' };
  if (a.includes('approve')) return { Icon: CheckCircle, tint: 'bg-emerald-50 text-emerald-600 ring-emerald-100' };
  if (a.includes('reject')) return { Icon: XCircle, tint: 'bg-rose-50 text-rose-600 ring-rose-100' };
  if (a.includes('transition')) return { Icon: ArrowRightLeft, tint: 'bg-violet-50 text-violet-700 ring-violet-100' };
  if (a.includes('lock')) return { Icon: Lock, tint: 'bg-slate-100 text-slate-700 ring-slate-200' };
  if (a.includes('reopen') || a.includes('unlock')) return { Icon: Unlock, tint: 'bg-cyan-50 text-cyan-700 ring-cyan-100' };
  if (a.includes('sync') || a.includes('resync')) return { Icon: RotateCw, tint: 'bg-cyan-50 text-cyan-700 ring-cyan-100' };
  // module-based fallback
  const m = (module || '').toLowerCase();
  if (m.includes('mom')) return { Icon: Gavel, tint: 'bg-violet-50 text-violet-700 ring-violet-100' };
  if (m.includes('ticket')) return { Icon: Ticket, tint: 'bg-amber-50 text-amber-700 ring-amber-100' };
  if (m.includes('bookmark')) return { Icon: Bookmark, tint: 'bg-amber-50 text-amber-700 ring-amber-100' };
  if (m.includes('credit')) return { Icon: Receipt, tint: 'bg-rose-50 text-rose-700 ring-rose-100' };
  if (m.includes('student') || m.includes('user')) return { Icon: UsersIcon, tint: 'bg-blue-50 text-blue-700 ring-blue-100' };
  if (m.includes('weekly')) return { Icon: ClipboardList, tint: 'bg-indigo-50 text-indigo-700 ring-indigo-100' };
  if (m.includes('notification')) return { Icon: Bell, tint: 'bg-amber-50 text-amber-700 ring-amber-100' };
  if (m.includes('report') || m.includes('incentive')) return { Icon: FileText, tint: 'bg-violet-50 text-violet-700 ring-violet-100' };
  if (m.includes('basky') || m.includes('ai')) return { Icon: Sparkles, tint: 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-100' };
  if (m.includes('system') || m.includes('settings')) return { Icon: SettingsIcon, tint: 'bg-slate-100 text-slate-700 ring-slate-200' };
  if (m.includes('sync')) return { Icon: Database, tint: 'bg-cyan-50 text-cyan-700 ring-cyan-100' };
  return { Icon: Activity, tint: 'bg-slate-100 text-slate-600 ring-slate-200' };
}

const PAGE_SIZE = 30;

function dayLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  const isSame = (a, b) => a.toDateString() === b.toDateString();
  if (isSame(d, today)) return 'Today';
  if (isSame(d, yesterday)) return 'Yesterday';
  return format(d, 'EEE, dd MMM yyyy');
}

export default function ActivityTimelinePage() {
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterMeta, setFilterMeta] = useState({ users: [], modules: [], actions: [] });
  const [stats, setStats] = useState({ total: 0, today: 0, last_7_days: 0, by_day: [] });

  // filters
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [userId, setUserId] = useState('all');
  const [module, setModule] = useState('all');
  const [action, setAction] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (userId !== 'all') params.user_id = userId;
      if (module !== 'all') params.module = module;
      if (action !== 'all') params.action = action;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = `${dateTo}T23:59:59.999Z`;
      if (searchDebounced.trim()) params.search = searchDebounced.trim();
      const res = await api.get('/activity', { params });
      setActivities(res.data.activities);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch {} finally { setLoading(false); }
  }, [page, userId, module, action, dateFrom, dateTo, searchDebounced]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { setPage(1); }, [userId, module, action, dateFrom, dateTo, searchDebounced]);

  useEffect(() => {
    (async () => {
      try {
        const [f, s] = await Promise.all([
          api.get('/activity/filters'),
          api.get('/activity/stats'),
        ]);
        setFilterMeta(f.data);
        setStats(s.data);
      } catch {}
    })();
  }, []);

  const grouped = useMemo(() => {
    const groups = [];
    let currentKey = null;
    for (const a of activities) {
      const key = dayLabel(a.created_at);
      if (key !== currentKey) {
        groups.push({ key, items: [] });
        currentKey = key;
      }
      groups[groups.length - 1].items.push(a);
    }
    return groups;
  }, [activities]);

  const hasFilters = userId !== 'all' || module !== 'all' || action !== 'all' || dateFrom || dateTo || search;

  const clearFilters = () => {
    setUserId('all'); setModule('all'); setAction('all');
    setDateFrom(''); setDateTo(''); setSearch('');
  };

  const sparklineMax = Math.max(1, ...stats.by_day.map(d => d.count));

  return (
    <div data-testid="activity-timeline-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
            <Activity className="w-7 h-7 text-violet-600" />
            Activity Timeline
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every change made by every user, across every module. Logs are retained for 6 months.
          </p>
        </div>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border border-border rounded-sm shadow-none">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Total events</p>
            <p className="font-data text-2xl font-bold text-slate-800 mt-1" data-testid="stat-total">{stats.total.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border border-border rounded-sm shadow-none">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Today</p>
            <p className="font-data text-2xl font-bold text-violet-700 mt-1" data-testid="stat-today">{stats.today.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border border-border rounded-sm shadow-none">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Last 7 days</p>
            <div className="flex items-end gap-3 mt-1">
              <p className="font-data text-2xl font-bold text-slate-800" data-testid="stat-7d">{stats.last_7_days.toLocaleString()}</p>
              <div className="flex items-end gap-0.5 h-7 flex-1">
                {stats.by_day.map((d, i) => (
                  <div
                    key={i}
                    title={`${d.date}: ${d.count}`}
                    className="flex-1 bg-violet-200 rounded-sm"
                    style={{ height: `${(d.count / sparklineMax) * 100}%`, minHeight: '2px' }}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter bar */}
      <Card className="border border-border rounded-sm shadow-none">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="activity-search"
                placeholder="Search by user, action, details…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 h-9 rounded-sm"
              />
            </div>

            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="w-[200px] h-9 rounded-sm" data-testid="filter-user">
                <SelectValue placeholder="User" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                {filterMeta.users.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-xs text-muted-foreground">({u.count})</span></SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={module} onValueChange={setModule}>
              <SelectTrigger className="w-[170px] h-9 rounded-sm" data-testid="filter-module">
                <SelectValue placeholder="Module" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modules</SelectItem>
                {filterMeta.modules.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="w-[180px] h-9 rounded-sm" data-testid="filter-action">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All actions</SelectItem>
                {filterMeta.actions.map(a => (
                  <SelectItem key={a} value={a}>{prettyAction(a)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="w-3.5 h-3.5" />
              <span>From</span>
            </div>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[160px] h-9 rounded-sm" data-testid="filter-date-from" />
            <span className="text-xs text-muted-foreground">to</span>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[160px] h-9 rounded-sm" data-testid="filter-date-to" />

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters" className="h-9 rounded-sm text-slate-600 ml-auto">
                <X className="w-3.5 h-3.5 mr-1" /> Clear filters
              </Button>
            )}
            <span className="ml-auto text-xs text-muted-foreground font-data">{total.toLocaleString()} events</span>
          </div>

          {hasFilters && (
            <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-slate-100">
              <FilterIcon className="w-3 h-3 text-slate-400" />
              {userId !== 'all' && <Badge variant="secondary" className="rounded-sm text-[11px]">User: {filterMeta.users.find(u => u.id === userId)?.name}</Badge>}
              {module !== 'all' && <Badge variant="secondary" className="rounded-sm text-[11px]">Module: {module}</Badge>}
              {action !== 'all' && <Badge variant="secondary" className="rounded-sm text-[11px]">Action: {prettyAction(action)}</Badge>}
              {dateFrom && <Badge variant="secondary" className="rounded-sm text-[11px]">From {dateFrom}</Badge>}
              {dateTo && <Badge variant="secondary" className="rounded-sm text-[11px]">To {dateTo}</Badge>}
              {searchDebounced && <Badge variant="secondary" className="rounded-sm text-[11px]">"{searchDebounced}"</Badge>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card className="border border-border rounded-sm shadow-none">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500" />
            </div>
          ) : activities.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-400">
              <Activity className="w-10 h-10 mb-2 opacity-50" />
              <p className="text-sm">No activity matches the current filters.</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[60vh]">
              {grouped.map(group => (
                <div key={group.key}>
                  <div className="sticky top-0 z-10 bg-white px-5 py-2 border-b border-slate-100">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{group.key}</p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {group.items.map(a => {
                      const { Icon, tint } = actionVisual(a.action, a.module);
                      const time = a.created_at ? format(new Date(a.created_at), 'HH:mm') : '';
                      const ago = a.created_at ? formatDistanceToNow(new Date(a.created_at), { addSuffix: true }) : '';
                      const isSystem = a.user_id === 'system';
                      return (
                        <div
                          key={a.id}
                          data-testid={`timeline-row-${a.id}`}
                          className="grid grid-cols-[auto_1fr_auto] items-start gap-4 px-5 py-3 hover:bg-slate-50 transition-colors"
                        >
                          <div className={`w-9 h-9 rounded-md flex items-center justify-center ring-1 ${tint} mt-0.5`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className={`font-semibold text-[13px] ${isSystem ? 'text-slate-500' : 'text-slate-800'}`}>{a.user_name || 'System'}</span>
                              {a.user_role && <Badge variant="outline" className="text-[10px] rounded-sm py-0 px-1.5 capitalize">{a.user_role}</Badge>}
                              <Badge variant="secondary" className="rounded-sm text-[10px] py-0 px-1.5 bg-violet-50 text-violet-700 hover:bg-violet-100">{a.module || a.resource_type || 'system'}</Badge>
                              <span className="text-[12px] text-slate-600">{prettyAction(a.action)}</span>
                            </div>
                            <p className="text-[12px] text-slate-500 mt-0.5 break-words">{a.details}</p>
                          </div>
                          <div className="text-right whitespace-nowrap">
                            <p className="font-data text-[11px] text-slate-700">{time}</p>
                            <p className="font-data text-[10px] text-slate-400">{ago}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page" className="rounded-sm">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page" className="rounded-sm">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
